import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";

// --- Type Definitions ---
export interface IProjectGallery {
  Detail: string[];
}

export interface IProjectProgress {
  General: number;
  Phase1: number;
  Phase2: number;
  Phase3: number;
}

export interface IProjectDetail {
  Title: string;
  Description: string;
}

export interface IProjectDetailsProperties {
  City: string;
  Gallery: IProjectGallery;
  Paragraphs: string[];
  ProjectDetails: IProjectDetail[];
  // ProjectLocation: string;
  // Progress: IProjectProgress;
  // ProjectType: string;
  // ProjectCost: string;
  // Client: string;
}

export interface IProjectDetailsWidget {
  type: string; // WidgetType.ProjectDetails in parent
  order: number;
  Title: string;
  Properties: IProjectDetailsProperties;
}

// --- Props for the ProjectDetailsItem component ---
interface ProjectDetailsItemProps {
  item: IProjectDetailsWidget;
  error: any;
  onChange: (updatedItem: IProjectDetailsWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ProjectDetailsItem: React.FC<ProjectDetailsItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Top-level widget Title.
  const widgetTitle: string = typeof item.Title === "string" ? item.Title : "";

  // Ensure properties exist.
  const properties = item.Properties || {
    City: "",
    Gallery: { Detail: [] },
    Paragraphs: [],
    ProjectDetails: [],
    // ProjectLocation: "",
    // Progress: { General: 0, Phase1: 0, Phase2: 0, Phase3: 0 },
    // ProjectType: "",
    // ProjectCost: "",
    // Client: "",
  };

  // Helper: if the value is an event, extract e.target.value; otherwise, use it.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handlers for top-level field.
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  // Handlers for properties fields.
  const handlePropertyChange = (
    field: keyof Omit<IProjectDetailsProperties, "Gallery" | "Paragraphs" | "ProjectDetails">,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Properties: { ...properties, [field]: getValue(e) } }, "Properties");
  };

  // // Handlers for Progress (we assume numbers are entered as strings and then converted).
  // const handleProgressChange = (
  //   field: keyof IProjectProgress,
  //   e: React.ChangeEvent<HTMLInputElement> | string
  // ) => {
  //   const valueStr = getValue(e);
  //   const num = parseFloat(valueStr) || 0;
  //   onChange(
  //     { ...item, Properties: { ...properties, Progress: { ...properties.Progress, [field]: num } } },
  //     "Properties"
  //   );
  // };

  // ----- ProjectDetails Handlers -----
  const projectDetails: IProjectDetail[] = Array.isArray(properties.ProjectDetails)
    ? properties.ProjectDetails
    : [];

  const handleProjectDetailChange = (
    index: number,
    field: keyof IProjectDetail,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedDetails = [...projectDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: newValue };
    onChange({ ...item, Properties: { ...properties, ProjectDetails: updatedDetails } }, "Properties");
  };

  const handleAddProjectDetail = () => {
    if (projectDetails.length >= 4) {
      return; // Max 4 items
    }
    const updatedDetails = [...projectDetails, { Title: "", Description: "" }];
    onChange({ ...item, Properties: { ...properties, ProjectDetails: updatedDetails } }, "Properties");
  };

  const handleRemoveProjectDetail = (index: number) => {
    const updatedDetails = projectDetails.filter((_, i) => i !== index);
    onChange({ ...item, Properties: { ...properties, ProjectDetails: updatedDetails } }, "Properties");
  };

  // ----- Paragraphs Handlers -----
  const paragraphs: string[] = Array.isArray(properties.Paragraphs) ? properties.Paragraphs : [];
  const handleParagraphChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newText = getValue(e);
    const updatedParagraphs = [...paragraphs];
    updatedParagraphs[index] = newText;
    onChange({ ...item, Properties: { ...properties, Paragraphs: updatedParagraphs } }, "Properties");
  };

  const handleAddParagraph = () => {
    const updatedParagraphs = [...paragraphs, ""];
    onChange({ ...item, Properties: { ...properties, Paragraphs: updatedParagraphs } }, "Properties");
  };

  const handleRemoveParagraph = (index: number) => {
    const updatedParagraphs = paragraphs.filter((_, i) => i !== index);
    onChange({ ...item, Properties: { ...properties, Paragraphs: updatedParagraphs } }, "Properties");
  };

  // ----- Gallery Handlers -----
  const galleryImages: string[] = Array.isArray(properties.Gallery?.Detail)
    ? properties.Gallery.Detail
    : [];

  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);

  const handleOpenGalleryImagePicker = (index: number) => {
    setCurrentImageIndex(index);
    setImagePickerOpen(true);
  };

  const handleGalleryImageSelect = (selected: string | string[]) => {
    if (currentImageIndex !== null) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedImages = [...galleryImages];
      updatedImages[currentImageIndex] = newImage;
      onChange(
        { ...item, Properties: { ...properties, Gallery: { Detail: updatedImages } } },
        "Properties"
      );
    }
    setImagePickerOpen(false);
    setCurrentImageIndex(null);
  };

  const handleRemoveGalleryImage = (index: number) => {
    const updatedImages = galleryImages.filter((_, i) => i !== index);
    onChange(
      { ...item, Properties: { ...properties, Gallery: { Detail: updatedImages } } },
      "Properties"
    );
  };

  const handleAddGalleryImage = () => {
    // Append an empty slot and open picker for that slot.
    const updatedImages = [...galleryImages, ""];
    onChange(
      { ...item, Properties: { ...properties, Gallery: { Detail: updatedImages } } },
      "Properties"
    );
    setCurrentImageIndex(updatedImages.length - 1);
    setImagePickerOpen(true);
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Project Details Widget
        </Typography>

        {/* Overall Widget Title */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />

        {/* Properties Section */}
        <GenericInput
          type="text"
          name="City"
          title="City"
          value={properties.City}
          error={error?.Properties?.City || ""}
          onChange={(e) => handlePropertyChange("City", e)}
          onBlur={() => onblur("City")}
          disabled={false}
        />
        {/* <GenericInput
          type="text"
          name="ProjectLocation"
          title="Project Location"
          value={properties.ProjectLocation}
          error={error?.Properties?.ProjectLocation || ""}
          onChange={(e) => handlePropertyChange("ProjectLocation", e)}
          onBlur={() => onblur("ProjectLocation")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="ProjectType"
          title="Project Type"
          value={properties.ProjectType}
          error={error?.Properties?.ProjectType || ""}
          onChange={(e) => handlePropertyChange("ProjectType", e)}
          onBlur={() => onblur("ProjectType")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="ProjectCost"
          title="Project Cost"
          value={properties.ProjectCost}
          error={error?.Properties?.ProjectCost || ""}
          onChange={(e) => handlePropertyChange("ProjectCost", e)}
          onBlur={() => onblur("ProjectCost")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Client"
          title="Client"
          value={properties.Client}
          error={error?.Properties?.Client || ""}
          onChange={(e) => handlePropertyChange("Client", e)}
          onBlur={() => onblur("Client")}
          disabled={false}
        /> */}

        {/* Paragraphs Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Paragraphs
        </Typography>
        {paragraphs.map((para, index) => (
          <Box key={index}>
            <GenericTextAreaInput
              name={`Paragraph-${index}`}
              title={`Paragraph ${index + 1}`}
              value={para}
              error={error?.Properties?.Paragraphs?.[index] || ""}
              onChange={(e) => handleParagraphChange(index, e)}
              onBlur={() => onblur("Paragraph", index)}
              disabled={false}
            />
            <IconButton
              color="error"
              onClick={() => handleRemoveParagraph(index)}
              sx={{ ml: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button variant="contained" onClick={handleAddParagraph} sx={{ mb: 2 }}>
          Add Paragraph
        </Button>

        {/* Project Details Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Project Details
        </Typography>
        {projectDetails.map((detail, index) => (
          <Box key={index} sx={{ border: "1px solid #ddd", p: 2, mb: 2, borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Detail {index + 1}
            </Typography>
            <GenericInput
              type="text"
              name={`ProjectDetailTitle-${index}`}
              title="Title"
              value={detail.Title}
              error={error?.Properties?.ProjectDetails?.[index]?.Title || ""}
              onChange={(e) => handleProjectDetailChange(index, "Title", e)}
              onBlur={() => onblur("ProjectDetailTitle", index)}
              disabled={false}
            />
            <GenericTextAreaInput
              name={`ProjectDetailDescription-${index}`}
              title="Description"
              value={detail.Description}
              error={error?.Properties?.ProjectDetails?.[index]?.Description || ""}
              onChange={(e) => handleProjectDetailChange(index, "Description", e)}
              onBlur={() => onblur("ProjectDetailDescription", index)}
              disabled={false}
            />
            <IconButton
              color="error"
              onClick={() => handleRemoveProjectDetail(index)}
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="contained"
          onClick={handleAddProjectDetail}
          disabled={projectDetails.length >= 4}
          sx={{ mb: 2 }}
        >
          Add Project Detail {projectDetails.length >= 4 && "(Max 4 reached)"}
        </Button>

        {/* Progress Section */}
        {/* <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Progress
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <GenericInput
              type="number"
              name="General"
              title="General"
              value={properties.Progress.General.toString()}
              error={error?.Properties?.Progress?.General || ""}
              onChange={(e) => handleProgressChange("General", e)}
              onBlur={() => onblur("General")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <GenericInput
              type="number"
              name="Phase1"
              title="Phase1"
              value={properties.Progress.Phase1.toString()}
              error={error?.Properties?.Progress?.Phase1 || ""}
              onChange={(e) => handleProgressChange("Phase1", e)}
              onBlur={() => onblur("Phase1")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <GenericInput
              type="number"
              name="Phase2"
              title="Phase2"
              value={properties.Progress.Phase2.toString()}
              error={error?.Properties?.Progress?.Phase2 || ""}
              onChange={(e) => handleProgressChange("Phase2", e)}
              onBlur={() => onblur("Phase2")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <GenericInput
              type="number"
              name="Phase3"
              title="Phase3"
              value={properties.Progress.Phase3.toString()}
              error={error?.Properties?.Progress?.Phase3 || ""}
              onChange={(e) => handleProgressChange("Phase3", e)}
              onBlur={() => onblur("Phase3")}
              disabled={false}
            />
          </Grid>
        </Grid> */}

        {/* Gallery Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Project Gallery
        </Typography>
        <Grid container spacing={2}>
          {galleryImages.map((img, index) => (
            <Grid item xs={12} sm={4} md={3} key={index}>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  height: 150,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                }}
                onClick={() => handleOpenGalleryImagePicker(index)}
              >
                {img ? (
                  <img
                    src={img}
                    alt={`Gallery ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Select Image
                  </Typography>
                )}
                {img && (
                  <IconButton
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(255,255,255,0.7)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveGalleryImage(index);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
        <Button variant="contained" onClick={handleAddGalleryImage} sx={{ mt: 2 }}>
          Add Image
        </Button>
      </CardContent>
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleGalleryImageSelect}
        />
      )}
    </Card>
  );
};

export default ProjectDetailsItem;
